import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClearFormDataActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import {
  ACCOUNTTYPE,
  ProviderClass,
  WidgetLayoutAttributes,
} from '@finxone-platform/shared/sys-config-types';
import { FinxoneSelectDropdownComponent } from '@finxone-platform/ui-components';
import { Store } from '@ngxs/store';

@Component({
  selector: 'finxone-web-frontend-payment-rail-selector-widget',
  templateUrl: './payment-rail-selector-widget.component.html',
  standalone: true,
  imports: [FormsModule, FinxoneSelectDropdownComponent],
})
export class PaymentRailSelectorWidgetComponent implements OnInit {
  defaultPaymentRail = SEPAPaymentRailTypeEnum.SEPA_CREDIT_TRANSFER;

  visiblePaymentRailList = [{ id: 1, value: SEPAPaymentRailTypeEnum.SEPA_CREDIT_TRANSFER }];

  /**
   * this {@link toggleRailSelector} is being used fo hide/show the payment rail dropdown
   * @type {boolean}
   */
  toggleRailSelector: boolean;

  isEuroAccount: boolean;

  inputLabel: string;

  constructor(
    public dataIn: ProviderClass<WidgetLayoutAttributes>,
    private store: Store,
    private configService: ConfigService,
    private formSubmissionService: FormSubmissionService,
  ) {}

  ngOnInit(): void {
    this.verifyEuroAccountSelected();
    this.setWidgetConfigProperties();
  }

  private verifyEuroAccountSelected() {
    if (this.configService.getActiveConfigType().isWorkflow) {
      this.isEuroAccount = true;
    } else {
      /**
       * Check current account's currency with @default {EURO} to show payment rail for @type {SEPA}
       *
       */
      this.isEuroAccount = this.store.selectSnapshot(AccountState.getCurrentAccount)?.currency === 'EUR';
    }
  }

  private setWidgetConfigProperties() {
    if (this.dataIn != undefined) {
      this.toggleRailSelector = !!this.dataIn.attri.widgetProperties?.['toggleRailSelector'];
      this.defaultPaymentRail = this.dataIn.attri.widgetProperties?.['defaultPaymentRail'];
      if (this.dataIn?.attri.widgetProperties?.inputLabel) {
        this.inputLabel = this.dataIn?.attri.widgetProperties?.inputLabel;
      }

      if (this.dataIn.attri.widgetProperties?.['selectedVisiblePaymentRailList']?.length) {
        this.visiblePaymentRailList = this.dataIn.attri.widgetProperties?.['selectedVisiblePaymentRailList'];
        this.modifyPaymentRailList();
      }

      /**
       * Update the @type {SEPA} form data when {@link toggleRailSelector} is on
       * and currency is @default {EURO}.
       */
      if (this.isEuroAccount) {
        this.updateSEPAPaymentFormData(this.defaultPaymentRail as SEPAPaymentRailTypeEnum);
      }

      if (this.toggleRailSelector) {
        this.updatePaymentRailFlagFormData();
      }

      /**
       * Clear the @type {SEPA} form data when currency is different other than @default {EURO} or
       * {@link toggleRailSelector} is off.
       */
      if (!this.toggleRailSelector && !this.isEuroAccount) {
        this.store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG));
        this.store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT));
      } else if (!this.isEuroAccount) {
        this.store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT));
      }
    }
  }

  private updateSEPAPaymentFormData(paymentRail: SEPAPaymentRailTypeEnum) {
    this.formSubmissionService.submitIndividualForm({ paymentRail }, '', FormActionTypeEnum.SEPA_PAYMENT);
  }

  private updatePaymentRailFlagFormData() {
    this.formSubmissionService.submitIndividualForm(
      { toggleRailSelector: this.toggleRailSelector },
      '',
      FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG,
    );
  }

  onChangePaymentRail(paymentRail: string) {
    this.updateSEPAPaymentFormData(paymentRail as SEPAPaymentRailTypeEnum);
  }

  /**
   * Target2 payment rail will be used for Business beneficiary users,
   * Therefore, this function {@link modifyPaymentRailList} will filter out the Target2 based on account type * of beneficiary selection.
   */
  private modifyPaymentRailList() {
    const formStateData = this.store.selectSnapshot(FormActionState.getFormActionState);
    if (formStateData?.response?.formData?.['accountType'] === ACCOUNTTYPE.PERSONAL) {
      this.visiblePaymentRailList = this.visiblePaymentRailList.filter(
        (paymentRail) => paymentRail.value !== SEPAPaymentRailTypeEnum.TARGET2,
      );
    }
  }
}
